import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Subscription} from 'rxjs';
import {take} from 'rxjs/operators';
import {CollectionsService} from '../shared/services/collections.service';
import {StationsService} from '../shared/services/stations.service';
import {CollectionModel} from '../shared/models/collection.model';
import {MarkerModel} from '../shared/models/marker.model';
import {StationModel} from '../shared/models/station.model';
import {DataAvailabilityService} from "../shared/services/data-availability.service";
import {VariablesPublicNamesService} from "../shared/services/variables-public-names.service";
import {VariablesCategoriesService} from "../shared/services/variables-categories.service";
import {GraphService} from "../shared/services/graph.service";
import {VariablePublicNamesMap} from "../shared/utils/variable-display-name";

interface CollectionAvailableData {
  [variableName: string]: {
    station: string;
    fromDate: string;
    toDate: string;
    depths: number[];
  }[];
}

@Component({
  selector: 'app-product-page',
  templateUrl: './product-page.component.html',
  styleUrls: ['./product-page.component.scss']
})
export class ProductPageComponent implements OnInit, OnDestroy {
  collectionId: string | null = null;
  collection: CollectionModel;
  loading = false;
  myLatLng = {lat: 32.805749669727064, lng: 35.59280158681916};
  mapOptions: google.maps.MapOptions = {
    mapTypeId: 'satellite',
    center: this.myLatLng,
    zoom: 9,
    disableDefaultUI: true,
    /*    restriction: {
          latLngBounds: {
            north: 32.924263355079454,
            south: 32.68872949473118,
            west: 35.47329557597845,
            east: 35.7094218031393
          }
        }*/
  };
  selectedKey: string | null = null;
  markers: MarkerModel[] = [];
  defaultIconUrl = 'http://maps.google.com/mapfiles/ms/icons/red-dot.png';
  selectedIconUrl = 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png';
  allStations: StationModel[] = [];
  collectionAvailableData: CollectionAvailableData;
  variableKeys: string[] = [];
  datasetStations: string[] = [];
  /**
   * #23 — configured public variable names, keyed by technical identifier. Fetched per
   * page init and passed down to the Dataset Data tab; no app-lifetime cache, so an Admin
   * rename shows up on the next page load.
   */
  publicNamesMap: VariablePublicNamesMap = {};
  /**
   * #24 — the variables the Dataset Charts tab may offer: the configured chart-enabled
   * keys intersected with what this dataset actually has data for. Deliberately a second,
   * independent list — `variableKeys` stays the unfiltered availability list that Dataset
   * Data, station discovery, the map markers and the date range depend on.
   */
  chartEnabledKeys: string[] = [];
  /**
   * #24 — the creation gate. `<app-product-page-charts>` (and therefore `<app-graph>`,
   * which replays the stored selection and issues a `/graph` request on init) is not
   * instantiated until eligibility has been resolved and the stored selection pruned, so a
   * disabled or unavailable variable cannot reach a request.
   */
  chartEligibilityResolved = false;
  private subscriptions: Subscription[] = [];


  constructor(
    private route: ActivatedRoute,
    private collectionsService: CollectionsService,
    private dataAvailabilityService: DataAvailabilityService,
    private stationsService: StationsService,
    private variablesPublicNamesService: VariablesPublicNamesService,
    private variablesCategoriesService: VariablesCategoriesService,
    private graphService: GraphService
  ) {
  }

  ngOnInit(): void {
    this.collectionId = this.route.snapshot.paramMap.get('collectionId');
    if (!this.collectionId) return;

    this.loading = true;
    this.fetchPublicVariableNames();
    this.fetchStations();
  }

  /**
   * #23 — the labels for the Dataset Data variable chips and accordion. Fetched
   * independently of the availability/station chain so a slow or failed lookup degrades to
   * the technical identifiers rather than blocking the page.
   */
  private fetchPublicVariableNames(): void {
    const sub = this.variablesPublicNamesService.getVariablesPublicNames()
      .pipe(take(1))
      .subscribe({
        next: (map) => this.publicNamesMap = map ?? {},
        error: () => this.publicNamesMap = {}
      });

    this.subscriptions.push(sub);
  }

  private fetchStations(): void {
    const sub = this.stationsService.getStations().pipe(take(1)).subscribe(stations => {
      this.allStations = stations.filter((station: StationModel) => station.latitude && station.longitude);
      this.fetchCollectionById();
    });
    this.subscriptions.push(sub);
  }

  private fetchCollectionById(): void {
    const sub = this.collectionsService.getCollectionById(this.collectionId!)
      .pipe(take(1))
      .subscribe({
        next: (collection) => {
          this.collection = collection;
          this.fetchCollectionDetails();
        },
        error: () => this.loading = false
      });

    this.subscriptions.push(sub);
  }

  private fetchCollectionDetails(): void {
    if (this.collection) {
      const sub = this.dataAvailabilityService
        .collectionAvailableDataByCollectionTitle(this.collection.title)
        .pipe(take(1))
        .subscribe({
          next: (data) => {
            if (data) {
              this.collectionAvailableData = data;
              this.variableKeys = Object.keys(data).filter(key =>
                !['_id', '__v', 'title', 'public_title', 'category', 'isMonitoringStation', 'lastUpdated'].includes(key)
              );
              this.setDatasetStations();
              this.setStationMarkers();
              this.resolveChartEligibility();
            } else {
              this.applyChartEligibility([]);
            }
          },
          complete: () => {
            this.loading = false;
          },
          error: () => {
            this.loading = false;
            this.applyChartEligibility([]);
          }
        });

      this.subscriptions.push(sub);
    } else {
      this.loading = false;
      this.applyChartEligibility([]);
    }
  }

  /**
   * #24 — read which of this dataset's variables are explicitly enabled for graphing.
   *
   * Runs after the availability payload, because the eligible list is the configured keys
   * intersected with the keys this dataset actually has data for. The availability
   * response itself is not touched: Dataset Data, the stations, the markers and the date
   * range keep consuming it exactly as before.
   *
   * Eligibility is resolved by collection id alone — `collection.category` plays no part.
   * Nothing writes that field for Admin-created collections (it is `null` on every MED
   * dataset), and the chart-enable configuration is keyed by `variables[].collectionId`.
   */
  private resolveChartEligibility(): void {
    const sub = this.variablesCategoriesService
      .getChartEnabledColumnKeys(this.collection?._id ?? this.collectionId!)
      .pipe(take(1))
      .subscribe({
        next: (columnKeys) => this.applyChartEligibility(columnKeys),
        // The service already fails safe; this keeps the gate from hanging if it ever stops.
        error: () => this.applyChartEligibility([])
      });

    this.subscriptions.push(sub);
  }

  /**
   * #24 — intersect, prune, then open the gate. The order matters: the stored selection is
   * cleaned *before* `chartEligibilityResolved` allows the chart children to exist.
   */
  private applyChartEligibility(columnKeys: string[]): void {
    const availableKeys = new Set(this.variableKeys);

    // The intersection is what covers "disabled *or* unavailable". An empty result is a
    // legitimate outcome — an empty selector, never a raw-field fallback.
    this.chartEnabledKeys = (columnKeys ?? []).filter(key => availableKeys.has(key));

    this.pruneStoredDatasetPageSelection();
    this.chartEligibilityResolved = true;
  }

  /**
   * #24 — drop every stored dataset-page selection whose variable is no longer
   * chart-eligible. A plain synchronous read → filter → write; no subscriptions, no
   * timers, so it is guaranteed complete before the gate opens.
   *
   * Scope is deliberately narrow: only disabled/unavailable variables are removed. The
   * wider restored-state, request-timing and neutral-state work stays in #29.
   */
  private pruneStoredDatasetPageSelection(): void {
    const storageKey = this.graphService.getSelectedVariablesKey('dataset-page');
    const stored = this.graphService.getLocalStorageSelectedVariables(storageKey);

    if (!Array.isArray(stored) || stored.length === 0) {
      return;
    }

    const enabled = new Set(this.chartEnabledKeys);
    const kept = stored.filter((item: any) => enabled.has(item?.column));

    if (kept.length === stored.length) {
      return;
    }

    if (kept.length === 0) {
      this.graphService.deleteLocalStorageSelectedVariables(storageKey);
    } else {
      this.graphService.setLocalStorageSelectedVariables(storageKey, kept);
    }
  }

  private setDatasetStations(): void {
    const stationSet = new Set<string>();
    this.variableKeys.forEach(key => {
      this.collectionAvailableData[key].forEach(entry => stationSet.add(entry.station));
    });
    this.datasetStations = Array.from(stationSet).sort();
  }

  private setStationMarkers(): void {
    this.markers = [];

    const activeSet = new Set(this.datasetStations);

    this.allStations.filter(station => activeSet.has(station.title))
      .forEach((station, index) => {
        this.markers.push({
          _id: station._id,
          id: index,
          lat: Number(station.latitude),
          lng: Number(station.longitude),
          selected: station.selected ?? false,
          markerOptions: {
            icon: this.defaultIconUrl,
            label: {
              text: station.title,
              color: 'white'
            }
          },
          options: {
            animation: google.maps.Animation.BOUNCE
          }
        });
      });
  }

  updateActiveStation(key: string): void {
    this.selectedKey = key;
    if (!key) {
      this.setDatasetStations();
    } else {
      const stationSet = new Set<string>();
      this.collectionAvailableData[key].forEach(entry => stationSet.add(entry.station));
      this.datasetStations = Array.from(stationSet).sort();
    }
    this.setStationMarkers();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.collectionId = null;
  }
}
